const STORAGE_KEY = 'c4y.admin.mfa.loginCredentials';

export type MfaLoginCredentials = {
  email: string;
  password: string;
  displayName?: string;
  returnTo?: string;
};

function canUseSessionStorage(): boolean {
  return typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';
}

/** Persist operator email/password for MFA enroll/challenge until login completes or session is cleared. */
export function writeMfaLoginCredentials(credentials: MfaLoginCredentials): void {
  if (!canUseSessionStorage()) return;
  const email = credentials.email.trim();
  if (!email || !credentials.password) return;
  try {
    window.sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        email,
        password: credentials.password,
        displayName: credentials.displayName?.trim() || undefined,
        returnTo: credentials.returnTo,
      }),
    );
  } catch {
    /* private mode / blocked storage */
  }
}

export function readMfaLoginCredentials(): MfaLoginCredentials | null {
  if (!canUseSessionStorage()) return null;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<MfaLoginCredentials>;
    const email = typeof parsed.email === 'string' ? parsed.email.trim() : '';
    const password = typeof parsed.password === 'string' ? parsed.password : '';
    if (!email || !password) return null;
    return {
      email,
      password,
      displayName:
        typeof parsed.displayName === 'string' && parsed.displayName.trim()
          ? parsed.displayName.trim()
          : undefined,
      returnTo: typeof parsed.returnTo === 'string' ? parsed.returnTo : undefined,
    };
  } catch {
    return null;
  }
}

export function clearMfaLoginCredentials(): void {
  if (!canUseSessionStorage()) return;
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/** Router location state wins; sessionStorage covers refresh / remount between MFA steps. */
export function resolveMfaLoginCredentials(
  locationState: Partial<MfaLoginCredentials> | null | undefined,
): MfaLoginCredentials | null {
  const email = locationState?.email?.trim() ?? '';
  const password = locationState?.password ?? '';
  if (email && password) {
    const resolved: MfaLoginCredentials = { email, password };
    const displayName = locationState?.displayName?.trim();
    if (displayName) resolved.displayName = displayName;
    if (locationState?.returnTo) resolved.returnTo = locationState.returnTo;
    return resolved;
  }
  return readMfaLoginCredentials();
}
